import * as React from "react";

import { Form, Input, InputNumber, Radio } from "antd";

import AuthorizationWrapper from "src/common/AuthorizationWrapper";

import OrganizationAPI from "./API/OrganizationAPI";
import PurgeOrganizationModal from "./PurgeOrganizationModal";

import { ClientAutoRedactionState, Validation } from "src/common/utils";
import { Button } from "src/components/buttons";
import { AdminPermissions } from "src/common/Permissions";
import { ModalOverlay, Spinner } from "src/components/common";
import { toast } from "react-toastify";

interface IProps {
	history?: any;
	match?: any;
	picklistItems: PickListItemResponseDTO[];
	currentUser: CurrentUserDTO;
}

interface IState {
	isActive: boolean;
	organization: OrganizationDTO;
	cognitiveValues: string[];
	productValues: string[];
	isLoading: boolean;
	loading: boolean;
	iconLoading: boolean;
	isFirstRedactConfirmOpen: boolean;
	isSecondRedactConfirmOpen: boolean;
	isPurgeModalOpen: boolean;

	fileList: File[];
	fileUrl: string;
	organizationNames: string[];
}

const defaultOrganization: OrganizationDTO = {
	id: undefined,
	name: "",
	isActive: true,
	webIdPrefix: 0,
	logoUrl: "",
	automaticClientRedaction: ClientAutoRedactionState.None,
	maximumDaysUntilRedaction: undefined,
	reportSettingId: undefined,
	daysUntilLock: 30,
};

class OrganizationDetails extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			isActive: false,
			organization: defaultOrganization,
			productValues: [],
			cognitiveValues: [],
			isLoading: true,
			loading: false,
			iconLoading: false,
			isFirstRedactConfirmOpen: false,
			isSecondRedactConfirmOpen: false,
			isPurgeModalOpen: false,

			fileList: [],
			fileUrl: "",
			organizationNames: [],
		};

		//remapping events
		this.maximumDaysUntilRedactionChange =
			this.maximumDaysUntilRedactionChange.bind(this);
	}

	public componentDidMount() {
		const organizationId = Number(this.props.match.params.organizationId);
		if (organizationId && organizationId > 0) {
			// if we ae in "Edit" Organization Page
			OrganizationAPI.getOrganization({ id: organizationId })
				.then((response) => {
					this.setState({
						organization: response,
						fileUrl: response.logoUrl ? response.logoUrl : "",
					});
				})
				.then(() =>
					OrganizationAPI.getOrganizationNames({ id: organizationId })
				)
				.then((res) => {
					this.setState({
						organizationNames: res.filter(
							(orgName: string): boolean =>
								orgName !== this.state.organization.name
						),
					});
				})
				.then(() => {
					this.setState({ isLoading: false });
				});
		} else {
			OrganizationAPI.getOrganizationNames({ id: organizationId })
				.then((res) => {
					this.setState({ organizationNames: res });
				})
				.then(() => {
					this.setState({ isLoading: false });
				});
		}
	}

	public render() {
		const { organization, organizationNames, loading } = this.state;
		const validOrganization = {
			organizationName: Validation.runMultipleValidations(
				[
					Validation.validateStringIsNotEmpty,
					Validation.validateValueIsUnique(organizationNames),
				],
				["Please enter a name", "Organization already exists"],
				organization.name!
			),
			organizationAutomaticClientRedaction: Validation.validateStringIsNotEmpty(
				organization.automaticClientRedaction!,
				"Auto redaction cannot be null"
			),
			organizationMaximumDaysUntilRedaction: Validation.validateIsEnum(
				organization.maximumDaysUntilRedaction!,
				organization.automaticClientRedaction!
			),
			organizationDaysUntilLockRange: Validation.validateNumberRange(
				organization.daysUntilLock!,
				30,
				100
			),
		};

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<div className="container-fluid">
				<div className="row">
					<div className="col-6 form-group">
						<label htmlFor="name">Organization name</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={
								validOrganization.organizationName.validationStatus
							}
							help={validOrganization.organizationName.message}
						>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<Input
									placeholder="Enter an organization name"
									name="name"
									value={organization.name!}
									onChange={this.onOrgChange}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-6 form-group">
						<label>Active</label>
						<Form.Item>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<Radio.Group
									name="isActive"
									onChange={this.onRadioGroupChange}
									value={organization.isActive}
								>
									<Radio key={`Active`} value={true}>
										Active
									</Radio>
									<Radio key={`Inactive`} value={false}>
										Inactive
									</Radio>
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="col-3 form-group">
						<label>Automatic organization info redaction</label>
						<Form.Item
							hasFeedback={true}
							validateStatus={
								validOrganization.organizationAutomaticClientRedaction
									.validationStatus
							}
							help={
								validOrganization.organizationAutomaticClientRedaction.message
							}
						>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<Radio.Group
									name="automaticClientRedaction"
									onChange={this.automaticClientRedactionChange}
									value={organization.automaticClientRedaction!}
								>
									<Radio
										value={ClientAutoRedactionState.None}
										checked={
											organization.automaticClientRedaction ===
											ClientAutoRedactionState.None
										}
									>
										None
									</Radio>
									<Radio
										value={ClientAutoRedactionState.Timed}
										checked={
											organization.automaticClientRedaction ===
											ClientAutoRedactionState.Timed
										}
									>
										Timed
									</Radio>
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-3 form-group">
						<label>Maximum days left until redaction</label>
						<Form.Item
							required={true}
							hasFeedback={true}
							validateStatus={
								validOrganization.organizationMaximumDaysUntilRedaction
									.validationStatus
							}
							help={
								validOrganization.organizationMaximumDaysUntilRedaction.message
							}
							className="mb-1"
						>
							<InputNumber
								value={organization.maximumDaysUntilRedaction!}
								placeholder="Maximum days left until redaction"
								onChange={this.maximumDaysUntilRedactionChange}
								min={1}
								max={10000}
								disabled={
									organization.automaticClientRedaction ===
									ClientAutoRedactionState.None
								}
								type="number"
								precision={0}
							/>
						</Form.Item>
					</div>
					<ModalOverlay
						isOpen={this.state.isFirstRedactConfirmOpen}
						modalSize="modal-lg"
						headerChildren="Organization redaction confirmation"
						children="This organization has been set to inactive. Would you also like to REDACT ALL PII from this organization? You will be asked to confirm a second time."
						confirmButtonChildren="Yes, redact information"
						cancelButtonChildren="No, save normally"
						cancelButtonAction={this.submitOrganization}
						confirmButtonAction={() => {
							this.setState({
								isSecondRedactConfirmOpen: true,
								isFirstRedactConfirmOpen: false,
							});
						}}
						onRequestClose={() => {
							this.setState({ isFirstRedactConfirmOpen: false });
						}}
					/>

					<ModalOverlay
						isOpen={this.state.isSecondRedactConfirmOpen}
						modalSize="modal-lg"
						headerChildren="Organization redaction confirmation"
						children="Are you sure you want to REDACT ALL PII from this organization? There is no going back"
						confirmButtonChildren="YES, I'm sure"
						cancelButtonChildren="NO, save normally"
						cancelButtonAction={this.submitOrganization}
						confirmButtonAction={this.submitOrganizationRedactConfirm}
						onRequestClose={() => {
							this.setState({ isSecondRedactConfirmOpen: false });
						}}
					/>

					<div className="col-3 form-group">
						<label>Days before sessions are locked</label>
						<Form.Item
							hasFeedback={true}
							required={true}
							validateStatus={
								validOrganization.organizationDaysUntilLockRange
									.validationStatus
							}
							help={validOrganization.organizationDaysUntilLockRange.message}
						>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<InputNumber
									value={organization.daysUntilLock}
									placeholder="30 to 100"
									name="daysUntilLock"
									onChange={this.onDaysUntilLockChange}
									min={30}
									max={100}
									type="number"
									precision={0}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="col-3 form-group">
						<label>Show Commercial Driving Experience</label>
						<Form.Item>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<Radio.Group
									name="showCommDrivingExp"
									onChange={this.onShowDrivingExpChange}
									value={organization.showCommDrivingExp}
								>
									<Radio key={`Active`} value={true}>
										Show
									</Radio>
									<Radio key={`Inactive`} value={false}>
										Hide
									</Radio>
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-3 form-group">
						<label>Require Commercial Driving Experience</label>
						<Form.Item>
							<AuthorizationWrapper
								permissions={[AdminPermissions.EditOrganization]}
								type="disable"
							>
								<Radio.Group
									name="requireCommDrivingExp"
									onChange={this.onRequireDrivingExpChange}
									value={organization.requireCommDrivingExp}
								>
									<Radio key={`Active`} value={true}>
										Require
									</Radio>
									<Radio key={`Inactive`} value={false}>
										Do not require
									</Radio>
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<AuthorizationWrapper
					permissions={[AdminPermissions.EditOrganization]}
					type="hide"
				>
					<Button
						className="btn-primary"
						disabled={!this.isValid(validOrganization)}
						onClick={
							organization.isActive
								? this.submitOrganization
								: () => {
										this.setState({ isFirstRedactConfirmOpen: true });
								  }
						}
						loading={loading}
					>
						Save
					</Button>
				</AuthorizationWrapper>
				{/* The default organization cannot be purged (server also refuses it).
				    The check stays outside the wrapper: it clones its child, and cloning
				    `false` renders an undefined element type (blank page). */}
				{!this.state.organization.isDefault && (
					<AuthorizationWrapper
						permissions={[AdminPermissions.GlobalAdmin]}
						type="hide"
					>
						<Button
							className="btn-danger"
							style={{ marginLeft: "0.5rem" }}
							onClick={() => this.setState({ isPurgeModalOpen: true })}
						>
							Purge Organization
						</Button>
					</AuthorizationWrapper>
				)}
				<PurgeOrganizationModal
					isOpen={this.state.isPurgeModalOpen}
					organizationId={this.state.organization.id!}
					organizationName={this.state.organization.name!}
					onClose={() => this.setState({ isPurgeModalOpen: false })}
				/>
			</div>
		);
	}

	private automaticClientRedactionChange = (e: any) => {
		const organization = { ...this.state.organization };
		if (e.target.value === false) {
			organization.maximumDaysUntilRedaction = undefined;
			organization.automaticClientRedaction = e.target.value;
			this.setState({ organization });
		} else {
			organization.automaticClientRedaction = e.target.value;
			this.setState({ organization });
		}
	};

	private maximumDaysUntilRedactionChange = (value: any) => {
		const { organization } = { ...this.state };
		if (value !== "" && value !== undefined && !isNaN(value)) {
			organization.maximumDaysUntilRedaction = value;
			this.setState({ organization });
		} else {
			return;
		}
	};

	private onDaysUntilLockChange = (value: any) => {
		const { organization } = { ...this.state };
		if (value !== "" && value !== undefined && !isNaN(value)) {
			organization.daysUntilLock = value;
			this.setState({ organization });
		} else {
			return;
		}
	};

	private onOrgChange = (e: any) => {
		let value = e.target.value!;
		if (value === "true") {
			value = true;
		}
		if (value === "false") {
			value = false;
		}
		let name = e.target.name;
		if (e.target.type === "radio") {
			value = value;
		} else if (e.target.type === "checkbox") {
			if (e.target.data !== undefined) {
				// If there's data then it's a checkbox group
				if (e.target.data.singleSelect) {
					value = value;
				} else {
					// Don't split if single value
					if (value === "") {
						value = [];
					} else {
						value = value.split(",");
					}
				}
				name = e.target.data.name;
			} else {
				value = e.target.checked;
			}
		}
		const organization = {
			...this.state.organization,
			[name]: value,
		};
		this.setState({ organization });
	};

	private isValid = (validOrganization: any) => {
		const validateValues: string[] = Object.keys(validOrganization).map(
			(key) => validOrganization[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private organizationLogoFile = () => {
		return this.state.fileList[0];
	};

	private submitOrganizationWithLogo = (organization: OrganizationDTO) => {
		const organizationLogoFile = this.organizationLogoFile();
		this.setState({ loading: true });
		OrganizationAPI.postOrganizationLogo(organizationLogoFile)
			.then((logoUrl) => {
				organization.logoUrl = logoUrl;
				this.setState({ organization });
			})
			.then(() => {
				let toastMessage = "Error creating new organization";
				if (organization.id !== undefined) {
					toastMessage = `Error updating organization!`;
				}
				OrganizationAPI.updateOrganization(organization)
					.then(({ id, name }) => {
						this.setState({ loading: false });
						if (this.props.match.params.organizationId <= 0) {
							// if on new Org page, redirect to Edit page, else redirect to list page
							toast.success("Successfully created Organization");
							this.props.history.push(`/admin/organizations/${id}`);
						} else {
							toast.success("Organization updated");
						}
						this.setState({ loading: false });
					})
					.catch((err) => {
						toast.error(toastMessage);
						this.setState({ loading: false });
					});
			})
			.catch((err) => {
				toast.error("Error uploading logo");
				this.setState({ loading: false });
			});
	};

	private submitOrganizationWithoutLogo = (organization: OrganizationDTO) => {
		this.setState({ loading: true });

		OrganizationAPI.updateOrganization(organization)
			.then(({ id, name }) => {
				if (this.props.match.params.organizationId <= 0) {
					// if on new Org page, redirect to Edit page, else redirect to list page
					toast.success("Successfully created Organization");
					this.props.history.push(`/admin/organizations/${id}`);
				} else {
					toast.success("Organization updated");
				}
				this.setState({ loading: false });
			})
			.catch((err) => {
				toast.error(err.response.data.responseStatus.message);
				this.setState({ loading: false });
			});
	};

	private submitOrganization = () => {
		const { organization, fileList } = this.state;

		if (!organization.isActive) {
			this.setState({
				isFirstRedactConfirmOpen: false,
				isSecondRedactConfirmOpen: false,
			});
		}

		if (fileList.length > 0) {
			this.submitOrganizationWithLogo(organization);
		} else {
			this.submitOrganizationWithoutLogo(organization);
		}
	};

	private submitOrganizationRedactConfirm = () => {
		const { organization } = this.state;
		organization.redacted = true;
		this.setState({ organization });
		this.submitOrganization();
	};

	private onRadioGroupChange = (e: any) => {
		const organization = { ...this.state.organization };
		organization.isActive = e.target.value;
		if (organization.isActive === true) {
			organization.redacted = false;
		}
		this.setState({ organization });
	};

	private onShowDrivingExpChange = (e: any) => {
		const organization = { ...this.state.organization };
		organization.showCommDrivingExp = e.target.value;
		if (organization.showCommDrivingExp === false) {
			organization.requireCommDrivingExp = false;
		}
		this.setState({ organization });
	};

	private onRequireDrivingExpChange = (e: any) => {
		const organization = { ...this.state.organization };
		organization.requireCommDrivingExp = e.target.value;
		this.setState({ organization });
	};
}

export default OrganizationDetails;
